// Theme system: add new themes here and they auto-appear in the theme switcher
// Each theme is a flat map of CSS variable names → values

export interface Theme {
  id: string;
  label: string;
  variables: Record<string, string>;
}

export const darkIndustrial: Theme = {
  id: 'dark-industrial',
  label: 'Dark Industrial',
  variables: {
    // Base
    '--bg-primary': '#0a0f1a',
    '--bg-secondary': '#0f1829',
    '--bg-tertiary': '#152035',
    '--bg-card': '#111d2e',
    '--bg-card-hover': '#162438',
    '--border': '#1e3454',
    '--border-light': '#253f66',

    // Text
    '--text-primary': '#e8f0fe',
    '--text-secondary': '#8bafd4',
    '--text-muted': '#4a6990',

    // Brand accent — electric cyan
    '--accent-primary': '#00c8ff',
    '--accent-hover': '#33d4ff',
    '--accent-dim': 'rgba(0, 200, 255, 0.12)',
    '--accent-glow': '0 0 20px rgba(0, 200, 255, 0.25)',

    // Semantic
    '--success': '#00e676',
    '--success-dim': 'rgba(0, 230, 118, 0.12)',
    '--warning': '#ffb300',
    '--warning-dim': 'rgba(255, 179, 0, 0.12)',
    '--danger': '#ff1744',
    '--danger-dim': 'rgba(255, 23, 68, 0.12)',

    // Severity colours
    '--severity-high': '#ff1744',
    '--severity-medium': '#ffb300',
    '--severity-low': '#00e676',

    // Typography
    '--font-display': "'Exo 2', 'Barlow', system-ui, sans-serif",
    '--font-body': "'IBM Plex Sans', 'Source Sans 3', system-ui, sans-serif",
    '--font-mono': "'IBM Plex Mono', 'Fira Code', monospace",

    // Radii
    '--radius-sm': '4px',
    '--radius-md': '8px',
    '--radius-lg': '12px',

    // Shadows
    '--shadow-card': '0 4px 24px rgba(0, 0, 0, 0.4)',
    '--shadow-glow': '0 0 40px rgba(0, 200, 255, 0.08)',
  },
};

export const lightPrecision: Theme = {
  id: 'light-precision',
  label: 'Light Precision',
  variables: {
    '--bg-primary': '#f4f7fb',
    '--bg-secondary': '#ffffff',
    '--bg-tertiary': '#edf1f7',
    '--bg-card': '#ffffff',
    '--bg-card-hover': '#f0f5ff',
    '--border': '#dde4ef',
    '--border-light': '#eaeff8',

    '--text-primary': '#0d1b2a',
    '--text-secondary': '#3d5a80',
    '--text-muted': '#7a93ae',

    '--accent-primary': '#0062cc',
    '--accent-hover': '#0050a8',
    '--accent-dim': 'rgba(0, 98, 204, 0.10)',
    '--accent-glow': '0 0 20px rgba(0, 98, 204, 0.15)',

    '--success': '#00875a',
    '--success-dim': 'rgba(0, 135, 90, 0.10)',
    '--warning': '#c96500',
    '--warning-dim': 'rgba(201, 101, 0, 0.10)',
    '--danger': '#d91f3a',
    '--danger-dim': 'rgba(217, 31, 58, 0.10)',

    '--severity-high': '#d91f3a',
    '--severity-medium': '#c96500',
    '--severity-low': '#00875a',

    '--font-display': "'Exo 2', 'Barlow', system-ui, sans-serif",
    '--font-body': "'IBM Plex Sans', 'Source Sans 3', system-ui, sans-serif",
    '--font-mono': "'IBM Plex Mono', 'Fira Code', monospace",

    '--radius-sm': '4px',
    '--radius-md': '8px',
    '--radius-lg': '12px',

    '--shadow-card': '0 2px 12px rgba(0, 0, 0, 0.08)',
    '--shadow-glow': '0 0 40px rgba(0, 98, 204, 0.06)',
  },
};

// Registry: add new themes here
export const themes: Theme[] = [darkIndustrial, lightPrecision];

export const defaultThemeId = 'dark-industrial';

export function applyTheme(theme: Theme) {
  const root = document.documentElement;
  Object.entries(theme.variables).forEach(([key, value]) => {
    root.style.setProperty(key, value);
  });
  root.setAttribute('data-theme', theme.id);
  localStorage.setItem('theme', theme.id);
}

export function loadTheme(): Theme {
  const saved = localStorage.getItem('theme');
  return themes.find(t => t.id === saved) ?? darkIndustrial;
}
